import { useEffect, useState } from 'react';
import type { LineHeightPreset, MarginPreset, NovelLayout } from '../components/ReaderSettings';
import { getStorageItem, setStorageItem } from '../utils/storage';

const LAYOUT_STORAGE_KEY = 'novelflow_layout';
const FONT_SIZE_STORAGE_KEY = 'novelflow_fontSize';
const LINE_HEIGHT_STORAGE_KEY = 'novelflow_lineHeight';
const HORIZONTAL_FONT_SIZE_STORAGE_KEY = 'novelflow_horizontal_fontSize';
const HORIZONTAL_LINE_HEIGHT_STORAGE_KEY = 'novelflow_horizontal_lineHeight';
const FONT_FAMILY_STORAGE_KEY = 'novelflow_fontFamily';
const MARGIN_STORAGE_KEY = 'novelflow_margin';

// 新規設定のストレージキー
const PAGE_TRANSITION_STORAGE_KEY = 'novelflow_pageTransition';
const BOOK_MODE_STORAGE_KEY = 'novelflow_bookModeEnabled';
const HAPTICS_ENABLED_STORAGE_KEY = 'novelflow_hapticsEnabled';
const SOUND_VOLUME_STORAGE_KEY = 'novelflow_soundVolume';
const BGM_ENABLED_STORAGE_KEY = 'novelflow_bgmEnabled';
const BGM_VOLUME_STORAGE_KEY = 'novelflow_bgmVolume';
const BGM_SOUND_STORAGE_KEY = 'novelflow_bgmSound';
const GLOW_ENABLED_STORAGE_KEY = 'novelflow_glowEnabled';
const GLOW_INTENSITY_STORAGE_KEY = 'novelflow_glowIntensity';
const FOCUS_MODE_ENABLED_STORAGE_KEY = 'novelflow_focusModeEnabled';
const FOCUS_LINE_COUNT_STORAGE_KEY = 'novelflow_focusLineCount';
const TATE_CHU_YOKO_ENABLED_STORAGE_KEY = 'novelflow_tateChuYokoEnabled';
const RADIAL_MENU_ENABLED_STORAGE_KEY = 'novelflow_radialMenuEnabled';
const BOOKMARK_GESTURE_ENABLED_STORAGE_KEY = 'novelflow_bookmarkGestureEnabled';
const READING_SPEED_STORAGE_KEY = 'novelflow_readingSpeed';
const SHARE_CARD_ENABLED_STORAGE_KEY = 'novelflow_shareCardEnabled';
const RUBY_DISPLAY_STORAGE_KEY = 'novelflow_rubyDisplay';
const SHOW_REMAINING_TIME_STORAGE_KEY = 'novelflow_showRemainingTime';
const EMPHASIS_DOTS_STYLE_STORAGE_KEY = 'novelflow_emphasisDotsStyle';

export type PageTransitionType = 'snap' | 'fade-slide' | 'curl';
export type BgmSoundType = 'rain' | 'cafe' | 'wave' | 'none';
export type RubyDisplayType = 'show' | 'dim' | 'hide';
export type EmphasisDotsStyleType =
  | 'filled-sesame'
  | 'filled-circle'
  | 'open-circle'
  | 'filled-triangle'
  | 'none';

const DEFAULT_LAYOUT: NovelLayout = 'vertical';
const DEFAULT_FONT_SIZE = 20;
const DEFAULT_LINE_HEIGHT: LineHeightPreset = 'normal';
const DEFAULT_HORIZONTAL_FONT_SIZE = 20;
const DEFAULT_HORIZONTAL_LINE_HEIGHT: LineHeightPreset = 'relaxed';
const DEFAULT_FONT_FAMILY: 'serif' | 'sans' = 'serif';
const DEFAULT_MARGIN: MarginPreset = 'normal';

const getSavedLayout = (): NovelLayout => {
  return getStorageItem<NovelLayout>(LAYOUT_STORAGE_KEY, DEFAULT_LAYOUT);
};

const getSavedFontSize = (layout: NovelLayout): number => {
  const defaultSize = layout === 'horizontal' ? DEFAULT_HORIZONTAL_FONT_SIZE : DEFAULT_FONT_SIZE;
  const savedHorizontal =
    layout === 'horizontal' ? getStorageItem(HORIZONTAL_FONT_SIZE_STORAGE_KEY, '') : '';
  const savedLegacy = getStorageItem(FONT_SIZE_STORAGE_KEY, '');
  const saved = savedHorizontal || savedLegacy;
  return saved ? Number.parseInt(saved, 10) : defaultSize;
};

const getSavedLineHeight = (layout: NovelLayout): LineHeightPreset => {
  const defaultLh = layout === 'horizontal' ? DEFAULT_HORIZONTAL_LINE_HEIGHT : DEFAULT_LINE_HEIGHT;
  const savedHorizontal =
    layout === 'horizontal'
      ? getStorageItem<LineHeightPreset | ''>(HORIZONTAL_LINE_HEIGHT_STORAGE_KEY, '')
      : '';
  const savedLegacy = getStorageItem<LineHeightPreset | ''>(LINE_HEIGHT_STORAGE_KEY, '');
  return (savedHorizontal || savedLegacy || defaultLh) as LineHeightPreset;
};

// 状態のインターフェース
interface ReaderSettingsState {
  layout: NovelLayout;
  verticalFontSize: number;
  horizontalFontSize: number;
  verticalLineHeight: LineHeightPreset;
  horizontalLineHeight: LineHeightPreset;
  fontFamily: 'serif' | 'sans';
  margin: MarginPreset;
  pageTransition: PageTransitionType;
  bookModeEnabled: boolean;
  hapticsEnabled: boolean;
  soundVolume: number;
  bgmEnabled: boolean;
  bgmVolume: number;
  bgmSound: BgmSoundType;
  glowEnabled: boolean;
  glowIntensity: number;
  focusModeEnabled: boolean;
  focusLineCount: number;
  tateChuYokoEnabled: boolean;
  radialMenuEnabled: boolean;
  bookmarkGestureEnabled: boolean;
  readingSpeed: number;
  shareCardEnabled: boolean;
  rubyDisplay: RubyDisplayType;
  showRemainingTime: boolean;
  emphasisDotsStyle: EmphasisDotsStyleType;
}

// 初期化関数
const getInitialSettings = (): ReaderSettingsState => ({
  layout: getSavedLayout(),
  verticalFontSize: getSavedFontSize('vertical'),
  horizontalFontSize: getSavedFontSize('horizontal'),
  verticalLineHeight: getSavedLineHeight('vertical'),
  horizontalLineHeight: getSavedLineHeight('horizontal'),
  fontFamily: getStorageItem<'serif' | 'sans'>(FONT_FAMILY_STORAGE_KEY, DEFAULT_FONT_FAMILY),
  margin: getStorageItem<MarginPreset>(MARGIN_STORAGE_KEY, DEFAULT_MARGIN),
  pageTransition: getStorageItem<PageTransitionType>(PAGE_TRANSITION_STORAGE_KEY, 'fade-slide'),
  bookModeEnabled: getStorageItem<string>(BOOK_MODE_STORAGE_KEY, 'false') === 'true',
  hapticsEnabled: getStorageItem<string>(HAPTICS_ENABLED_STORAGE_KEY, 'true') === 'true',
  soundVolume: Number.parseFloat(getStorageItem<string>(SOUND_VOLUME_STORAGE_KEY, '0.5')),
  bgmEnabled: getStorageItem<string>(BGM_ENABLED_STORAGE_KEY, 'false') === 'true',
  bgmVolume: Number.parseFloat(getStorageItem<string>(BGM_VOLUME_STORAGE_KEY, '0.3')),
  bgmSound: getStorageItem<BgmSoundType>(BGM_SOUND_STORAGE_KEY, 'rain'),
  glowEnabled: getStorageItem<string>(GLOW_ENABLED_STORAGE_KEY, 'true') === 'true',
  glowIntensity: Number.parseFloat(getStorageItem<string>(GLOW_INTENSITY_STORAGE_KEY, '0.5')),
  focusModeEnabled: getStorageItem<string>(FOCUS_MODE_ENABLED_STORAGE_KEY, 'false') === 'true',
  focusLineCount: Number.parseInt(getStorageItem<string>(FOCUS_LINE_COUNT_STORAGE_KEY, '3'), 10),
  tateChuYokoEnabled: getStorageItem<string>(TATE_CHU_YOKO_ENABLED_STORAGE_KEY, 'true') === 'true',
  radialMenuEnabled: getStorageItem<string>(RADIAL_MENU_ENABLED_STORAGE_KEY, 'false') === 'true',
  bookmarkGestureEnabled:
    getStorageItem<string>(BOOKMARK_GESTURE_ENABLED_STORAGE_KEY, 'true') === 'true',
  readingSpeed: Number.parseInt(getStorageItem<string>(READING_SPEED_STORAGE_KEY, '600'), 10),
  shareCardEnabled: getStorageItem<string>(SHARE_CARD_ENABLED_STORAGE_KEY, 'false') === 'true',
  rubyDisplay: getStorageItem<RubyDisplayType>(RUBY_DISPLAY_STORAGE_KEY, 'show'),
  showRemainingTime: getStorageItem<string>(SHOW_REMAINING_TIME_STORAGE_KEY, 'true') === 'true',
  emphasisDotsStyle: getStorageItem<EmphasisDotsStyleType>(
    EMPHASIS_DOTS_STYLE_STORAGE_KEY,
    'filled-sesame',
  ),
});

let saveSettingsTimeout: ReturnType<typeof setTimeout> | null = null;

// 非同期でLocalStorageへ一括保存
const asyncSaveSettingsToStorage = (state: ReaderSettingsState) => {
  if (saveSettingsTimeout) {
    clearTimeout(saveSettingsTimeout);
  }

  saveSettingsTimeout = setTimeout(() => {
    const save = () => {
      setStorageItem(LAYOUT_STORAGE_KEY, state.layout);
      setStorageItem(FONT_SIZE_STORAGE_KEY, String(state.verticalFontSize));
      setStorageItem(HORIZONTAL_FONT_SIZE_STORAGE_KEY, String(state.horizontalFontSize));
      setStorageItem(LINE_HEIGHT_STORAGE_KEY, state.verticalLineHeight);
      setStorageItem(HORIZONTAL_LINE_HEIGHT_STORAGE_KEY, state.horizontalLineHeight);
      setStorageItem(FONT_FAMILY_STORAGE_KEY, state.fontFamily);
      setStorageItem(MARGIN_STORAGE_KEY, state.margin);
      setStorageItem(PAGE_TRANSITION_STORAGE_KEY, state.pageTransition);
      setStorageItem(BOOK_MODE_STORAGE_KEY, String(state.bookModeEnabled));
      setStorageItem(HAPTICS_ENABLED_STORAGE_KEY, String(state.hapticsEnabled));
      setStorageItem(SOUND_VOLUME_STORAGE_KEY, String(state.soundVolume));
      setStorageItem(BGM_ENABLED_STORAGE_KEY, String(state.bgmEnabled));
      setStorageItem(BGM_VOLUME_STORAGE_KEY, String(state.bgmVolume));
      setStorageItem(BGM_SOUND_STORAGE_KEY, state.bgmSound);
      setStorageItem(GLOW_ENABLED_STORAGE_KEY, String(state.glowEnabled));
      setStorageItem(GLOW_INTENSITY_STORAGE_KEY, String(state.glowIntensity));
      setStorageItem(FOCUS_MODE_ENABLED_STORAGE_KEY, String(state.focusModeEnabled));
      setStorageItem(FOCUS_LINE_COUNT_STORAGE_KEY, String(state.focusLineCount));
      setStorageItem(TATE_CHU_YOKO_ENABLED_STORAGE_KEY, String(state.tateChuYokoEnabled));
      setStorageItem(RADIAL_MENU_ENABLED_STORAGE_KEY, String(state.radialMenuEnabled));
      setStorageItem(BOOKMARK_GESTURE_ENABLED_STORAGE_KEY, String(state.bookmarkGestureEnabled));
      setStorageItem(READING_SPEED_STORAGE_KEY, String(state.readingSpeed));
      setStorageItem(SHARE_CARD_ENABLED_STORAGE_KEY, String(state.shareCardEnabled));
      setStorageItem(RUBY_DISPLAY_STORAGE_KEY, state.rubyDisplay);
      setStorageItem(SHOW_REMAINING_TIME_STORAGE_KEY, String(state.showRemainingTime));
      setStorageItem(EMPHASIS_DOTS_STYLE_STORAGE_KEY, state.emphasisDotsStyle);
    };

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(save);
    } else {
      save();
    }
    saveSettingsTimeout = null;
  }, 100);
};

let globalSettingsState: ReaderSettingsState | null = null;
const settingsListeners = new Set<(state: ReaderSettingsState) => void>();

const getGlobalSettings = (): ReaderSettingsState => {
  if (!globalSettingsState || settingsListeners.size === 0) {
    globalSettingsState = getInitialSettings();
  }
  return globalSettingsState;
};

const notifySettingsListeners = (nextState: ReaderSettingsState) => {
  globalSettingsState = nextState;
  for (const listener of settingsListeners) {
    listener(nextState);
  }
};

export const useReaderSettings = () => {
  const [state, setState] = useState<ReaderSettingsState>(getGlobalSettings);

  useEffect(() => {
    const listener = (nextState: ReaderSettingsState) => {
      setState(nextState);
    };
    settingsListeners.add(listener);
    return () => {
      settingsListeners.delete(listener);
      if (settingsListeners.size === 0) {
        globalSettingsState = null;
      }
    };
  }, []);

  const updateState = (updates: Partial<ReaderSettingsState>) => {
    const current = getGlobalSettings();
    const next = { ...current, ...updates };
    notifySettingsListeners(next);
    asyncSaveSettingsToStorage(next);
  };

  const fontSize =
    state.layout === 'horizontal' ? state.horizontalFontSize : state.verticalFontSize;
  const lineHeight =
    state.layout === 'horizontal' ? state.horizontalLineHeight : state.verticalLineHeight;

  const setFontSize = (size: number | ((prev: number) => number)) => {
    const current = getGlobalSettings();
    const isH = current.layout === 'horizontal';
    const currentSize = isH ? current.horizontalFontSize : current.verticalFontSize;
    const newSize = typeof size === 'function' ? size(currentSize) : size;
    const next = isH
      ? { ...current, horizontalFontSize: newSize }
      : { ...current, verticalFontSize: newSize };
    notifySettingsListeners(next);
    asyncSaveSettingsToStorage(next);
  };

  const setLineHeight = (
    height: LineHeightPreset | ((prev: LineHeightPreset) => LineHeightPreset),
  ) => {
    const current = getGlobalSettings();
    const isH = current.layout === 'horizontal';
    const currentHeight = isH ? current.horizontalLineHeight : current.verticalLineHeight;
    const newHeight = typeof height === 'function' ? height(currentHeight) : height;
    const next = isH
      ? { ...current, horizontalLineHeight: newHeight }
      : { ...current, verticalLineHeight: newHeight };
    notifySettingsListeners(next);
    asyncSaveSettingsToStorage(next);
  };

  return {
    layout: state.layout,
    setLayout: (layout: NovelLayout) => updateState({ layout }),
    fontSize,
    setFontSize,
    lineHeight,
    setLineHeight,
    fontFamily: state.fontFamily,
    setFontFamily: (fontFamily: 'serif' | 'sans') => updateState({ fontFamily }),
    margin: state.margin,
    setMargin: (margin: MarginPreset) => updateState({ margin }),
    pageTransition: state.pageTransition,
    setPageTransition: (pageTransition: PageTransitionType) => updateState({ pageTransition }),
    bookModeEnabled: state.bookModeEnabled,
    setBookModeEnabled: (bookModeEnabled: boolean) => updateState({ bookModeEnabled }),
    hapticsEnabled: state.hapticsEnabled,
    setHapticsEnabled: (hapticsEnabled: boolean) => updateState({ hapticsEnabled }),
    soundVolume: state.soundVolume,
    setSoundVolume: (soundVolume: number) => updateState({ soundVolume }),
    bgmEnabled: state.bgmEnabled,
    setBgmEnabled: (bgmEnabled: boolean) => updateState({ bgmEnabled }),
    bgmVolume: state.bgmVolume,
    setBgmVolume: (bgmVolume: number) => updateState({ bgmVolume }),
    bgmSound: state.bgmSound,
    setBgmSound: (bgmSound: BgmSoundType) => updateState({ bgmSound }),
    glowEnabled: state.glowEnabled,
    setGlowEnabled: (glowEnabled: boolean) => updateState({ glowEnabled }),
    glowIntensity: state.glowIntensity,
    setGlowIntensity: (glowIntensity: number) => updateState({ glowIntensity }),
    focusModeEnabled: state.focusModeEnabled,
    setFocusModeEnabled: (focusModeEnabled: boolean) => updateState({ focusModeEnabled }),
    focusLineCount: state.focusLineCount,
    setFocusLineCount: (focusLineCount: number) => updateState({ focusLineCount }),
    tateChuYokoEnabled: state.tateChuYokoEnabled,
    setTateChuYokoEnabled: (tateChuYokoEnabled: boolean) => updateState({ tateChuYokoEnabled }),
    radialMenuEnabled: state.radialMenuEnabled,
    setRadialMenuEnabled: (radialMenuEnabled: boolean) => updateState({ radialMenuEnabled }),
    bookmarkGestureEnabled: state.bookmarkGestureEnabled,
    setBookmarkGestureEnabled: (bookmarkGestureEnabled: boolean) =>
      updateState({ bookmarkGestureEnabled }),
    readingSpeed: state.readingSpeed,
    setReadingSpeed: (readingSpeed: number) => updateState({ readingSpeed }),
    shareCardEnabled: state.shareCardEnabled,
    setShareCardEnabled: (shareCardEnabled: boolean) => updateState({ shareCardEnabled }),
    rubyDisplay: state.rubyDisplay,
    setRubyDisplay: (rubyDisplay: RubyDisplayType) => updateState({ rubyDisplay }),
    showRemainingTime: state.showRemainingTime,
    setShowRemainingTime: (showRemainingTime: boolean) => updateState({ showRemainingTime }),
    emphasisDotsStyle: state.emphasisDotsStyle,
    setEmphasisDotsStyle: (emphasisDotsStyle: EmphasisDotsStyleType) =>
      updateState({ emphasisDotsStyle }),
  };
};
